import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import { QuickSearchLightboxProps } from './types';
import { QuickSearchLightboxItem } from './QuickSearchLightboxItem';
import { colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { ArrowUpRight, Close, Search } from '@sweep-io/sweep-design/dist/icons';
import Stack from '@mui/material/Stack';
import SearchInputWithTooltip from '../SearchInputWithTooltip';
import { QuickSearchLightboxLoader } from './QuickSearchLightboxLoader';
import Divider from '@mui/material/Divider';

export const QuickSearchLightbox = <T,>({
  isOpen,
  isLoading,
  items,
  filterComponent,
  submittedInputValue,
  typedInputValue,
  listTitle,
  displayShowAllButton,
  renderListElement,
  onItemClick,
  onSearchChange,
  onKeyDown,
  onClose,
  onClearSearchButtonClick,
  onShowAllClick,
}: QuickSearchLightboxProps<T>) => {
  const displayTooltip = !!typedInputValue && typedInputValue !== submittedInputValue;

  const renderSearchInputWithTooltip = () => (
    <SearchInputWithTooltip
      displayTooltip={displayTooltip}
      onClearButtonClick={onClearSearchButtonClick}
      withFixedMagnifyingGlassIcon
      TextFieldProps={{
        onChange: onSearchChange,
        value: typedInputValue,
        placeholder: 'Search',
        onKeyDown,
      }}
      forceCloseButton={typedInputValue !== '' && submittedInputValue !== ''}
      autoFocus={true}
    />
  );

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Stack
        sx={{
          padding: '16px 16px 0 16px',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
        }}
      >
        {renderSearchInputWithTooltip()}
        {filterComponent && filterComponent}
        {onClose && (
          <IconButton onClick={onClose} variant="flat" size="tiny">
            <Close />
          </IconButton>
        )}
      </Stack>
      <DialogContent
        sx={{
          padding: '8px 16px',
          height: '455px',
          width: '673px',
          gap: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading && <QuickSearchLightboxLoader />}

        {!isLoading && (
          <Stack flex="1" minHeight={0} overflow="hidden">
            {items?.length === 0 && (
              <Stack direction="row" gap={1} alignItems="center" pl={1.5} mt={2} mb={2}>
                <Search color={colors.grey[700]} size="medium" />
                <Typography variant="caption" color={colors.grey[500]}>
                  No results
                </Typography>
              </Stack>
            )}

            {listTitle && items?.length > 0 && (
              <Stack pl={1.5} pt={1} pb={1}>
                <Typography variant="caption" color={colors.grey[500]}>
                  {listTitle}
                </Typography>
              </Stack>
            )}

            <Stack overflow="auto" flex="1" minHeight={0} pb={1}>
              {items.map((item) =>
                renderListElement && item.data ? (
                  renderListElement(item.data)
                ) : (
                  <QuickSearchLightboxItem
                    key={item.title}
                    item={item}
                    onItemClick={() => onItemClick?.(item)}
                  />
                ),
              )}
            </Stack>

            {displayShowAllButton && (
              <Stack minHeight="40px" alignItems="center">
                <Divider flexItem sx={{ marginBottom: 1 }} />
                <QuickSearchLightboxItem
                  key={'expandView'}
                  item={{
                    id: 'expandView',
                    title: 'Expand view',
                    icon: <ArrowUpRight size="medium" />,
                  }}
                  onItemClick={onShowAllClick}
                  isHighlighted={true}
                />
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
